import defaultFetch from 'isomorphic-unfetch';
import { stringify } from 'query-string';
import { partialRight } from 'ramda';

import { type FavouriteProductActionAPIResponse, type FavouriteProductsAPIResponse } from './types';
import { createCustomError } from '@/scopes/common/infrastructure/logging/create-custom-error';
import type { FavouritesApiClient, ProductParameters } from '@/scopes/common/domain/types/favourites';

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string;
  };
  fetch: typeof defaultFetch;
  queryStringSerializer: {
    serialize: typeof stringify;
  };
}

export const ProductAPISystemError = createCustomError('ProductAPISystemError');
export const ProductAPIUserNotIdentifiedError = createCustomError('ProductAPIUserNotIdentifiedError');
export const ProductAPIUserUnexpectedError = createCustomError('ProductAPIUserUnexpectedError');

export const createFavouritesApiClient = (dependencies: Partial<Dependencies> = {}): FavouritesApiClient => {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = { serialize: partialRight(stringify, [{ arrayFormat: 'index' }]) },
  } = dependencies as Dependencies;

  async function getFavouriteProducts(): Promise<string[]> {
    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/ProductFavourites/GetFavourites`;

    const response = await fetchWithOptions(url);

    const { accoIds } = (await response.json()) as FavouriteProductsAPIResponse;

    return accoIds.map(String);
  }

  async function addProductToFavourites({ contextItemId, productId }: ProductParameters): Promise<void> {
    const queryString = queryStringSerializer.serialize({
      contextItemId,
      accommodationId: productId,
    });

    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/ProductFavourites/AddFavourite?${queryString}`;

    const response = await fetchWithOptions(url);

    await handleFavouriteActionResponse(response);
  }

  async function removeProductFromFavourites({ contextItemId, productId }: ProductParameters): Promise<void> {
    const queryString = queryStringSerializer.serialize({
      contextItemId,
      accommodationId: productId,
    });

    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/ProductFavourites/RemoveFavourite?${queryString}`;

    const response = await fetchWithOptions(url);

    await handleFavouriteActionResponse(response);
  }

  async function fetchWithOptions(url: string) {
    const response = await fetch(url, {
      referrerPolicy: 'no-referrer-when-downgrade',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    responseOkOrThrow(response);

    return response;
  }

  async function handleFavouriteActionResponse(response: Response) {
    const data = await response.json();

    userIdentifiedOrThrow(data);
    actionSuccessOrThrow(data);
  }

  function responseOkOrThrow(response: Response) {
    if (!response.ok) {
      throw new ProductAPISystemError('Failed to add favourite due to system error');
    }
  }

  function userIdentifiedOrThrow({ success, isUserLoggedIn }: FavouriteProductActionAPIResponse) {
    if (!success && !isUserLoggedIn) {
      throw new ProductAPIUserNotIdentifiedError('Failed to add favourite, user not logged in');
    }
  }

  function actionSuccessOrThrow({ success }: FavouriteProductActionAPIResponse) {
    if (!success) {
      throw new ProductAPIUserUnexpectedError('Failed to add favourite, unexpected error');
    }
  }

  return {
    getFavouriteProducts,
    addProductToFavourites,
    removeProductFromFavourites,
  };
};
